import { theme, DeleteIcon, DeprecatedEditIcon } from '@arnold/common';
import { getLocalizedDateWithoutLeadingZeroes, removeAccents } from '@arnold/core';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import Highlighter from 'react-highlight-words';
import { TFunction } from 'react-i18next';
import {
  Features,
  FeEventType,
  NotstartedSurveyReason,
  OrganizationQuery,
  SurveyGroupRespondentImportType,
  SurveyGroupRespondentsQuery,
  SurveyGroupWithoutRespondentsQuery,
  SurveyStatus,
  UserQuery,
} from '../../../generated/hooks';
import { getFullName, goToTalkProcessResults } from '../../../lib/helpers';
import { useFeatureFlag } from '../../../lib/helpers/hooks';
import i18n from '../../../translations/i18n';
import { ButtonLink } from '../../StyledComponents';
import { EditParticipant } from '../../SurveyGroup/EditParticipantForm';
import { ParticipantDetail } from './ParticipantDetail/ParticipantDetail';
import { ProcessSurveyParticipantsData } from './types';
import { getParticipantStatus } from './utils';

type GetDataSourceParams = {
  surveyGroupRespondents?: SurveyGroupRespondentsQuery['surveyGroupRespondents']['data'];
  surveyGroup: NonNullable<SurveyGroupWithoutRespondentsQuery['surveyGroup']>;
  t: TFunction;
  topLevelReportAccess?: string | boolean;
  isOnboarding?: boolean;
  searchQuery: string;
  organization: UserQuery['user']['organization'] | OrganizationQuery['organization'];
  setIsEditModalOpen: (isOpen: EditParticipant | null) => void;
  setRemoveRespondentModal: (isOpen: EditParticipant | null) => void;
  handleLogFeEvent: (respondentId: string) => (eventType: FeEventType) => void;
};

const StyledLinkButton = styled(ButtonLink)`
  display: flex;
  align-items: center;
  -webkit-logical-height: auto;
  padding: 0;
  margin: 0;
  margin-left: ${({ moveLeft }: { moveLeft?: boolean }) => (moveLeft ? `-${theme.spacing.f}` : '0')};
`;

export const getDataSource = ({
  surveyGroupRespondents,
  t,
  topLevelReportAccess,
  surveyGroup,
  isOnboarding,
  searchQuery,
  organization,
  setIsEditModalOpen,
  setRemoveRespondentModal,
  handleLogFeEvent,
}: GetDataSourceParams): ProcessSurveyParticipantsData[] | undefined | null => {
  if (!surveyGroupRespondents) {
    return surveyGroupRespondents;
  }
  return surveyGroupRespondents.map((respondent) => {
    const reasons = respondent.surveys
      .filter((survey) => survey.notstartedReason)
      .map((survey) => survey.notstartedReason);
    const uniqueReasons = [...new Set(reasons)] as NotstartedSurveyReason[];

    const deletedAtRaw = respondent.deletedAt;
    const deletedAtString = typeof deletedAtRaw === 'string' ? deletedAtRaw.trim() : '';
    const deletedAtDate = deletedAtString ? new Date(deletedAtString) : null;
    const hasValidDeletedAt = !!deletedAtDate && !Number.isNaN(deletedAtDate.getTime());

    const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);
    const alreadyStartedSurveys = respondent.surveys.filter(
      (s) => ![SurveyStatus.Prepared, SurveyStatus.Notstarted].includes(s.status) && s.topicGroup,
    );

    return {
      id: `${respondent.respondent.id}-${respondent.addedOn}-${respondent.recruitmentId}`,
      surveyParticipant: (
        <span data-cy="process-survey-participant-name">
          <Highlighter
            highlightStyle={{
              fontWeight: '700',
              backgroundColor: 'transparent',
              color: theme.colorPrimary,
              padding: 0,
            }}
            textToHighlight={getFullName(respondent.respondent.firstname, respondent.respondent.surname, true) || ''}
            searchWords={searchQuery.split(' ')}
            sanitize={removeAccents}
          />
        </span>
      ),
      startDate: (
        <span
          data-cy="process-survey-participant-start-date"
          className="d-flex"
          style={{
            whiteSpace: 'nowrap',
          }}
        >
          {getLocalizedDateWithoutLeadingZeroes(new Date(respondent.addedOn), i18n.language)}{' '}
        </span>
      ),
      editDate:
        respondent.deleted ||
        !respondent.isLastRun ||
        respondent.respondent.deleted ||
        isEmployeeManagementDisabled ||
        (respondent.surveys.length > 0 &&
          respondent.surveys.every((s) =>
            [SurveyStatus.Completed, SurveyStatus.Archived].includes(s.status),
          )) ? null : (
          <StyledLinkButton
            moveLeft
            onClick={() => {
              handleLogFeEvent(respondent.respondent.id)(FeEventType.SurveyGroupChangeDate);
              setIsEditModalOpen({
                id: respondent.respondent.id,
                firstname: respondent.respondent.firstname ?? '',
                surname: respondent.respondent.surname ?? '',
                addedOn: respondent.addedOn,
                deleted: respondent.deleted,
                alreadyStartedSurveys,
                type: SurveyGroupRespondentImportType.ChangeDate,
              });
            }}
          >
            <DeprecatedEditIcon />
          </StyledLinkButton>
        ),
      recruiter: respondent.recruiterName ? respondent.recruiterName : '-',
      team: (
        <Highlighter
          highlightStyle={{
            fontWeight: '700',
            backgroundColor: 'transparent',
            padding: 0,
          }}
          textToHighlight={
            respondent.respondent.team?.name ? respondent.respondent.team.name : t('teams:withoutLeader')
          }
          searchWords={searchQuery.split(' ')}
          sanitize={removeAccents}
        />
      ),
      status: (
        <span
          data-cy="process-survey-participant-status"
          className="d-flex flex-column"
          style={{ gap: theme.spacing.b }}
        >
          <span>
            {getParticipantStatus(
              [
                ...respondent.surveys,
                ...(respondent.hiringSurveys || [])?.filter((s) =>
                  s.whitelistedRespondents.some((r) => r.id === respondent.respondent.id),
                ),
              ],
              true,
              organization,
              surveyGroup.status,
              t,
              uniqueReasons,
              respondent.deleted || respondent.respondent.deleted,
              respondent.isCreatingSurveys,
            )}
          </span>
          {(respondent.deleted || respondent.respondent.deleted) && hasValidDeletedAt && (
            <span style={{ color: theme.colors.text.secondary, fontSize: 12 }}>
              {getLocalizedDateWithoutLeadingZeroes(deletedAtDate!, i18n.language)}
            </span>
          )}
        </span>
      ),
      displayAnswers: respondent.channels?.some((c) => c.answered) && (
        <Button
          onClick={
            topLevelReportAccess
              ? () =>
                  goToTalkProcessResults(
                    topLevelReportAccess,
                    respondent.respondent.id,
                    respondent.addedOn.substr(0, 10),
                    '_blank',
                  )
              : undefined
          }
          variant={'outline-primary'}
          size="sm"
          disabled={!topLevelReportAccess}
        >
          {t('answers')}
        </Button>
      ),
      deleteButton:
        respondent.deleted ||
        respondent.respondent.deleted ||
        !respondent.isLastRun ||
        (respondent.deleted && respondent.surveys.length > 0) ||
        isEmployeeManagementDisabled ? null : (
          <StyledLinkButton
            data-cy="process-survey-participant-remove"
            onClick={() => {
              setRemoveRespondentModal({
                id: respondent.respondent.id,
                firstname: respondent.respondent.firstname ?? '',
                surname: respondent.respondent.surname ?? '',
                deleted: respondent.deleted,
                addedOn: respondent.addedOn,
                creditsToReturn: respondent.creditsToReturn ?? 0,
                alreadyStartedSurveys,
                channels: respondent.channels,
              });
            }}
          >
            <DeleteIcon />
          </StyledLinkButton>
        ),
      participantDetail: (
        <ParticipantDetail
          surveyGroup={surveyGroup}
          respondent={respondent.respondent}
          respondentGroup={respondent}
          isOnboarding={isOnboarding}
          organizationId={organization?.id!}
          lastRun={respondent.isLastRun}
        />
      ),
    };
  });
};
